// Session and trace ID management logic extracted from Emissary

import { v4 as uuidv4 } from 'uuid';

export interface SessionState {
  sessionId: string;
  traceId: string | null;
  lastProcessedMessage: string | null;
}

export class SessionManager {
  private sessionId: string;
  private traceId: string | null = null;
  private lastProcessedMessage: string | null = null;

  constructor(sessionId?: string) {
    this.sessionId = sessionId || uuidv4();
  }

  getSessionId(): string {
    return this.sessionId;
  }

  getTraceId(): string | null {
    return this.traceId;
  }

  createNewTraceId(prefix: string = 'ui'): string {
    this.traceId = `${prefix}-${Date.now()}-${uuidv4()}`;
    return this.traceId;
  }

  getLastProcessedMessage(): string | null {
    return this.lastProcessedMessage;
  }

  setLastProcessedMessage(message: string): void {
    this.lastProcessedMessage = message;
  }

  isMessageProcessed(message: string): boolean {
    return message === this.lastProcessedMessage;
  }

  reset(): void {
    this.traceId = null;
    this.lastProcessedMessage = null;
    // Keep sessionId as it persists for the entire conversation
  }

  getState(): SessionState {
    return {
      sessionId: this.sessionId,
      traceId: this.traceId,
      lastProcessedMessage: this.lastProcessedMessage
    };
  }
}

// Helper function to validate message before sending
export interface MessageValidationResult {
  isValid: boolean;
  reason?: string;
}

export function validateMessageForSending(
  message: string,
  currentUser: { email?: string } | null,
  assistantId: string | null
): MessageValidationResult {
  if (!message.trim()) {
    return { isValid: false, reason: 'Message is empty' };
  }

  if (!currentUser?.email) {
    return { isValid: false, reason: 'User not authenticated' };
  }

  if (!assistantId) {
    return { isValid: false, reason: 'No assistant selected' };
  }

  return { isValid: true };
}

// Helper function to determine if we should mark messages as read
export function shouldMarkMessagesAsRead(
  activeChat: 'bot' | 'human',
  currentUserEmail: string | null | undefined,
  assistantId: string | null
): boolean {
  return activeChat === 'human' && !!currentUserEmail && !!assistantId;
}

// Helper function to handle error states
export interface ErrorState {
  message: string;
  isRecoverable: boolean;
  preserveThinking?: boolean;
}

export function classifyError(error: unknown): ErrorState {
  if (error instanceof Error) {
    // Network errors
    if (error.message.includes('Network') || error.message.includes('fetch')) {
      return {
        message: 'Network error. Please check your connection.',
        isRecoverable: true,
        preserveThinking: true
      };
    }

    // Abort errors
    if (error.name === 'AbortError' || error.message.includes('aborted')) {
      return {
        message: 'Request was cancelled.',
        isRecoverable: true,
        preserveThinking: true
      };
    }

    // API errors
    if (error.message.includes('API') || error.message.includes('401') || error.message.includes('403')) {
      return {
        message: 'Authentication error. Please log in again.',
        isRecoverable: false,
        preserveThinking: false
      };
    }

    // Default error
    return {
      message: error.message || 'An unexpected error occurred.',
      isRecoverable: true,
      preserveThinking: true
    };
  }

  // Unknown error type
  return {
    message: 'An unexpected error occurred.',
    isRecoverable: true,
    preserveThinking: false
  };
}